import React, { useCallback, useEffect, useRef, useState } from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import { Grid, OrbitControls, PerspectiveCamera } from "@react-three/drei";

import GaussianPoints from "./GaussianPoints";
import GaussianSplats from "./GaussianSplats";
import { loadGaussianScene } from "./loaders/GaussianLoader";
import { debugLog } from "../../utils/debug";

const CAMERA_DIRECTION = new THREE.Vector3(1, 0.55, 1).normalize();

/**
 * Waits for React Three Fiber to release GPU resources from the
 * previous scene before beginning another large scene load.
 */
function waitForFrames(frameCount = 2) {
  return new Promise((resolve) => {
    let remaining = frameCount;

    function nextFrame() {
      remaining -= 1;

      if (remaining <= 0) {
        resolve();
        return;
      }

      requestAnimationFrame(nextFrame);
    }

    requestAnimationFrame(nextFrame);
  });
}

/**
 * Calculates a conservative bounding sphere from finite Gaussian
 * positions for automatic camera framing.
 */
function calculateSceneBounds(positions) {
  if (!positions || positions.length < 3) {
    return null;
  }

  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;

  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;

  for (let i = 0; i < positions.length; i += 3) {
    const x = positions[i];
    const y = positions[i + 1];
    const z = positions[i + 2];

    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
      continue;
    }

    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    minZ = Math.min(minZ, z);

    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
    maxZ = Math.max(maxZ, z);
  }

  if (!Number.isFinite(minX)) {
    return null;
  }

  const center = new THREE.Vector3(
    (minX + maxX) * 0.5,
    (minY + maxY) * 0.5,
    (minZ + maxZ) * 0.5,
  );

  const radius = Math.max(
    Math.hypot(maxX - minX, maxY - minY, maxZ - minZ) * 0.5,
    0.01,
  );

  return { center, radius };
}

/**
 * Loads Gaussian scene data and manages camera framing, representation
 * switching, and WebGL context recovery.
 */
export default function GaussianRenderer({
  source,
  filename,
  representation,
  splatScale,
  opacity,
  alphaCutoff,
  showGrid,
  resetCameraToken,
  onStatus,
  onSceneLoaded,
}) {
  const { gl, camera } = useThree();

  const [data, setData] = useState(null);

  const controlsRef = useRef(null);
  const cameraFrameRef = useRef(null);
  const loadGenerationRef = useRef(0);

  const applyCameraFrame = useCallback(() => {
    const frame = cameraFrameRef.current;
    const controls = controlsRef.current;

    if (!frame || !controls) {
      return;
    }

    const { center, radius } = frame;

    const halfFov = THREE.MathUtils.degToRad(camera.fov * 0.5);

    const distance = (radius / Math.tan(halfFov)) * 1.25;

    camera.position.copy(center).addScaledVector(CAMERA_DIRECTION, distance);

    camera.near = Math.max(distance / 1000, 0.01);

    camera.far = Math.max(distance + radius * 10, 100);

    camera.updateProjectionMatrix();

    controls.target.copy(center);
    controls.minDistance = Math.max(radius * 0.05, 0.01);

    controls.maxDistance = Math.max(radius * 20, distance * 4);

    camera.lookAt(center);
    controls.update();

    debugLog("[GaussianRenderer] Camera framed:", {
      center: center.toArray(),
      radius,
      distance,
    });
  }, [camera]);

  const loadScene = useCallback(async () => {
    // Each load receives an ID so stale async results cannot replace
    // a newer scene selected while the previous load was in progress.
    const generation = ++loadGenerationRef.current;

    try {
      setData(null);
      cameraFrameRef.current = null;
      onStatus?.("Loading...");

      await waitForFrames();

      if (generation !== loadGenerationRef.current) {
        return;
      }

      debugLog("[GaussianRenderer] Loading:", {
        source,
        filename,
        generation,
      });

      const result = await loadGaussianScene(source, filename);

      if (generation !== loadGenerationRef.current) {
        return;
      }

      debugLog("[GaussianRenderer] Loaded:", {
        format: result.format,
        count: result.count,
        generation,
      });

      cameraFrameRef.current = calculateSceneBounds(result.positions);

      setData(result);
      onStatus?.("Ready");
      onSceneLoaded?.(result);
    } catch (error) {
      if (generation !== loadGenerationRef.current) {
        return;
      }

      console.error("[GaussianRenderer] Load failed:", error);

      setData(null);
      cameraFrameRef.current = null;

      onStatus?.(error instanceof Error ? error.message : String(error));
    }
  }, [source, filename, onStatus, onSceneLoaded]);

  useEffect(() => {
    loadScene();
  }, [loadScene]);

  useEffect(() => {
    if (!data) {
      return undefined;
    }

    const frameId = requestAnimationFrame(applyCameraFrame);

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [data, applyCameraFrame]);

  useEffect(() => {
    if (data) {
      applyCameraFrame();
    }
  }, [resetCameraToken, data, applyCameraFrame]);

  useEffect(() => {
    const canvas = gl.domElement;

    function handleContextLost(event) {
      event.preventDefault();

      console.warn("[GaussianRenderer] WebGL context lost");

      onStatus?.("GPU context lost — recovering...");
    }

    function handleContextRestored() {
      console.warn("[GaussianRenderer] WebGL context restored");

      onStatus?.("Reloading scene...");
      loadScene();
    }

    canvas.addEventListener("webglcontextlost", handleContextLost);

    canvas.addEventListener("webglcontextrestored", handleContextRestored);

    return () => {
      canvas.removeEventListener("webglcontextlost", handleContextLost);

      canvas.removeEventListener("webglcontextrestored", handleContextRestored);
    };
  }, [gl, onStatus, loadScene]);

  return (
    <>
      <color attach="background" args={["#090b0a"]} />

      <PerspectiveCamera makeDefault position={[7, 4.5, 8]} fov={50} />

      {data &&
        (representation === "splats" ? (
          <GaussianSplats
            data={data}
            splatScale={splatScale}
            opacity={opacity}
            alphaCutoff={alphaCutoff}
            controlsRef={controlsRef}
          />
        ) : (
          <GaussianPoints data={data} />
        ))}

      {showGrid && (
        <Grid
          args={[20, 20]}
          position={[0, -0.03, 0]}
          cellSize={0.5}
          sectionSize={2}
          fadeDistance={20}
          infiniteGrid
        />
      )}

      <OrbitControls
        ref={controlsRef}
        makeDefault
        enableDamping
        dampingFactor={0.08}
      />
    </>
  );
}
